import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import StandardModal from './StandardModal';
import { PrimaryButton, SecondaryButton } from './Buttons';
import TimeSlotSelector from './TimeSlotSelector';
import { Search, Users, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatShortDate } from '../utils/dateFormatters';

/**
 * Quick availability check modal
 * Shows which teamMembers are available for a specific date/time
 */
const QuickAvailabilityModal = ({ isOpen, onClose, teamMembers, roles }) => {
  const [checkDate, setCheckDate] = useState('');
  const [checkTime, setCheckTime] = useState('');
  const [availableTeam, setAvailableTeam] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const DAYS = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];

  const checkAvailability = () => {
    if (!checkDate || !checkTime) {
      toast.error('Please select both date and time');
      return;
    }

    // Convert input date to YYYY-MM-DD format to match unavailableDates
    const inputDate = new Date(checkDate + 'T00:00:00');
    const checkDateStr = inputDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    const dayOfWeek = DAYS[inputDate.getDay()];

    console.log('Checking availability for:', { checkDateStr, checkTime, dayOfWeek });

    // Filter available team
    const available = teamMembers.filter(teamMember => {
      console.log(`Checking ${teamMember.name}:`);
      
      // Check if they're marked unavailable on this specific date
      const hasUnavailableDate = teamMember.unavailableDates?.some(date => {
        // Handle both YYYY-MM-DD and other formats
        const compareDate = date.includes('-') ? date : date;
        console.log(`  Unavailable dates: ${teamMember.unavailableDates?.join(', ')}`);
        console.log(`  Comparing ${compareDate} with ${checkDateStr}`);
        return compareDate === checkDateStr;
      });
      
      if (hasUnavailableDate) {
        console.log(`  ❌ ${teamMember.name} unavailable on ${checkDateStr}`);
        return false;
      }

      // Check if they have availability on this day
      const dayAvailability = teamMember.recurringAvailability?.find(avail => avail.day === dayOfWeek);
      if (!dayAvailability) {
        console.log(`  ❌ ${teamMember.name} doesn't work on ${dayOfWeek}`);
        return false;
      }

      // Check if the time falls within their availability
      const checkTimeMinutes = timeToMinutes(checkTime);
      const startMinutes = timeToMinutes(dayAvailability.startTime);
      const endMinutes = timeToMinutes(dayAvailability.endTime);

      const timeAvailable = checkTimeMinutes >= startMinutes && checkTimeMinutes <= endMinutes;
      console.log(`  Time check: ${checkTime} (${checkTimeMinutes}) within ${dayAvailability.startTime}-${dayAvailability.endTime} (${startMinutes}-${endMinutes})? ${timeAvailable}`);

      if (!timeAvailable) {
        console.log(`  ❌ ${teamMember.name} not available at ${checkTime}`);
        return false;
      }

      console.log(`  ✅ ${teamMember.name} IS available!`);
      return true;
    });

    setAvailableTeam(available);
    setHasSearched(true);

    if (available.length === 0) {
      toast.warning('No team available at this time');
    } else {
      toast.success(`Found ${available.length} available teamMember(s)`);
    }
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const resetModal = () => {
    setCheckDate('');
    setCheckTime('');
    setAvailableTeam([]);
    setHasSearched(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Quick Availability Check"
      description="Find teamMembers available for a specific date and time"
      size="md"
      footer={
        <SecondaryButton onClick={handleClose} className="flex-1">
          Close
        </SecondaryButton>
      }
    >
      <div className="space-y-6">
        {/* Search Inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="check-date">Date *</Label>
            <Input
              id="check-date"
              type="date"
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="check-time">Time *</Label>
            <TimeSlotSelector
              value={checkTime}
              onChange={setCheckTime}
              placeholder="Select time"
              className="mt-1"
            />
          </div>
        </div>

        <PrimaryButton onClick={checkAvailability} className="w-full">
          <Search className="w-4 h-4 mr-2" />
          Check Availability
        </PrimaryButton>

        {/* Results */}
        {hasSearched && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                Available Team ({availableTeam.length})
              </h3>
              <span className="text-xs text-gray-500">
                for {formatShortDate(checkDate)} at {checkTime}
              </span>
            </div>

            {availableTeam.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No team available at this time</p>
                <p className="text-sm mt-1">Try a different date or time</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableTeam.map(teamMember => {
                  const role = roles.find(r => r.id === teamMember.roleId);
                  return (
                    <div key={teamMember.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${role?.color || '#3B82F6'}20`, border: `2px solid ${role?.color || '#3B82F6'}` }}
                        >
                          <Users className="w-4 h-4" style={{ color: role?.color || '#3B82F6' }} />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{teamMember.name}</p>
                          <p className="text-xs text-gray-600">{teamMember.email || teamMember.phone}</p>
                        </div>
                      </div>
                      {teamMember.roleName && (
                        <Badge 
                          variant="outline"
                          style={{ borderColor: role?.color, color: role?.color }}
                          className="text-xs"
                        >
                          {teamMember.roleName}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </StandardModal>
  );
};

export default QuickAvailabilityModal;